<template>
  <div class="main-page-container">
    <AlertFoundActiveSession />
    <!-- header -->
    <div class="d-flex">
      <PageHeader
        :title="$t('complianceList.title')"
        :hasHeaderButton="true"
        class="page-top-header d-flex align-items-center vp-width-270"
      >
        <template #headerButton>
          <button class="cursor-pointer" @click="$router.push({ path: `/compliance-new` })" style="width: 165px">
            {{ $t('complianceList.createCompliance') }}
          </button>
        </template>
      </PageHeader>
    </div>

    <!-- exporting -->
    <b-modal id="exportingProgress" hide-footer hide-header size="lg" :scrollable="false">
      <ExportProgress />
    </b-modal>

    <b-modal
      id="compliance-data-modal"
      size="lg"
      class="popover-modal"
      hide-header
      hide-footer
      no-close-on-esc
      :scrollable="true"
      ref="compliance-data-modal"
    >
      <ViewComplianceData :complianceData="complianceData" />
    </b-modal>

    <b-modal
      id="deleteComplianceConfirmModal"
      size="lg"
      class="popover-modal"
      hide-header
      hide-footer
      no-close-on-esc
      :scrollable="true"
      ref="deleteComplianceConfirmModal">
      <VpConfirmModal :modalId="'deleteComplianceConfirmModal'">
        <template v-slot:title>
          <span>{{ $t('complianceList.deleteCompliance') }}</span>
        </template>

        <template v-slot:text1>
          <span>{{ $t('common.notes.areYouSureYouWant') }} <strong>"{{ $t('common.delete') }}"</strong> {{ complianceData.name }}{{ $t('common.questionMark') }}</span>
        </template>

        <template v-slot:buttons>
          <VpButton :text="$t('common.cancel')" @click="$bvModal.hide('deleteComplianceConfirmModal')" />
          <VpButton :text="$t('common.delete')" :disabled="submitted" :isDanger="true" @click="onDeleteCompliance()" />
        </template>
      </VpConfirmModal>
    </b-modal>

    <section id="complianceGrid">
      <!-- Grid Start-->
      <DxDataGrid
        id="gridContainer"
        :rtl-enabled="rtlEnabled"
        :ref="dataGridRef"
        :data-source="rowsData"
        :no-data-text="gridEmptyMessage"
        :showBorders="false"
        :columnAutoWidth="true"
        :allowColumnReordering="true"
        :allowColumnResizing="true"
        :rowAlternationEnabled="false"
        :showColumnLines="true"
        :showRowLines="true"
        @content-ready="contentReady($event)"
        @editor-preparing="onEditorPreparing($event)"
        column-resizing-mode="widget"
        :class="{
          complianceGridHeightWithAlert: getActiveSessionsCount,
          complianceGridHeight: !getActiveSessionsCount,
        }"
      >
        <DxScrolling mode="standard" :scroll-by-content="rtlEnabled" :use-native="!rtlEnabled" />

        <DxStateStoring :enabled="true" type="custom" :customLoad="customLoad" :customSave="customSave"
          storage-key="complianceGrid" />

        <DxPaging :page-size="50" />
        <DxPager
          :visible="true"
          :allowed-page-sizes="[5, 10, 25, 50, 100]"
          :display-mode="'full'"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
          :infoText="rtlEnabled ?  'صفحة {0} من {1} ({2} إجمالي العناصر)' : ''"
        />

        <DxFilterRow :visible="true" />

        <DxHeaderFilter :visible="true" />

        <DxColumnChooser
          :enabled="true"
          mode="select"
          :title="$t('common.grid.columnChooser')"
        />

        <DxExport :enabled="true" :allow-export-selected-data="false" />

        <DxToolbar>
          <!-- <DxItem name="groupPanel"/> -->
          <DxItem template="columnButtonTemplate" location="before" />
          <DxItem template="exportButtonTemplate" location="before" />
          <DxItem template="clearAllTemplate" location="before" />
        </DxToolbar>

        <DxColumn data-field="name" :caption="$t('common.name')" :allow-header-filtering="false" />
        <DxColumn data-field="version" :caption="$t('complianceList.grid.version')" alignment="left"
        :visible="getTableHeaders &&
                getTableHeaders.columns &&
                getTableHeaders.columns.find(
                  (item) => item.dataField == 'version'
                ) &&
                (getTableHeaders.columns.find(
                  (item) => item.dataField == 'version'
                ).visible ||
                  getTableHeaders.columns.find(
                    (item) => item.dataField == 'version'
                  ).visible == false)
              ? getTableHeaders.columns.find(
                  (item) => item.dataField == 'version'
                ).visible : false" />
        <DxColumn data-field="date" :caption="$t('common.date')" :allow-header-filtering="false" />
        <DxColumn :caption="$t('common.grid.actions')" :allow-exporting="false" cell-template="action-cell" />
        

        <template #clearAllTemplate>
          <div v-if="showClearAll" class="clear-cols d-flex align-items-center">
            <template>
              <div class="toolbar-custom-line"></div>
              <Button
                :text="$t('common.grid.clearFilters')"
                @click="reset()"
                styling-mode="outlined"
                type="danger"
                class="clear-CustomColums cursor-pointer"
              />
            </template>
          </div>
        </template>

        <template #exportButtonTemplate>
          <DxDropDownButton class="export-dropdown" :items="buttonData" :text="$t('common.grid.export')"
            @item-click="serverSideExport" />
        </template>

        <template #columnButtonTemplate>
          <button class="custom-columns" @click="dataGrid.showColumnChooser()">
            <span class="custom-text">{{ $t("common.grid.columns") }} </span>

            <span class="custom-icon">
              <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
            </span>
          </button>
        </template>

        <template #action-cell="{ data }">
          <ComplianceActionRenderer :data="data.data" @showComplianceEvent="showCompliance" />
        </template>
      </DxDataGrid>
      <!-- Grid End -->
    </section>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import PageHeader from "@/components/page-header";
import ComplianceActionRenderer from "./ComplianceActionsRenderer";
import { complianceColumns } from "@/utils/dataSource";
import { exportExel } from "@/utils/mixins/exportMixin";
import AlertFoundActiveSession from "@/components/emergency/AlertFoundActiveSession";
import complianceService from "@/services/compliance";
import { default as Button } from "devextreme-vue/button";
import DxDropDownButton from "devextreme-vue/drop-down-button";
import ViewComplianceData from "./viewComplianceData";
import ExportProgress from "@/components/grid/ExportProgress";
import DxDataGrid, {
  DxFilterRow,
  DxHeaderFilter,
  DxColumnChooser,
  DxColumn,
  DxExport,
  DxPaging,
  DxPager,
  DxStateStoring,
  DxScrolling,
  DxToolbar,
  DxItem,
} from "devextreme-vue/data-grid";
import "jspdf-autotable";
// import CustomStore from "devextreme/data/custom_store";
const dataGridRef = "dataGrid";
// const subGridRef = "dataGrid";
import "whatwg-fetch";

export default {
  title: "Compliance",
  mixins: [exportExel],
  components: {
    PageHeader,
    ComplianceActionRenderer,
    AlertFoundActiveSession,
    Button,
    DxDropDownButton,
    DxDataGrid,
    DxFilterRow,
    DxHeaderFilter,
    DxColumnChooser,
    DxColumn,
    DxExport,
    DxPaging,
    DxPager,
    DxStateStoring,
    DxScrolling,
    DxToolbar,
    DxItem,
    ViewComplianceData,
    ExportProgress
  },
  data: function () {
    return {
      showClearAll: false,
      showTimePanel: false,
      title: "Compliance List",
      complianceData: null,
      items: [
        {
          text: "Compliance",
          href: "/compliance",
        },
        {
          text: "List",
          active: true,
        },
      ],
      complianceColumns: complianceColumns,
      hideArrow: false,
      getComplianceList: [],
      dataGridRef,
      rtlEnabled: false,
      gridEmptyMessage: this.$t('common.grid.noRecordsYet'),
      buttonData: ["Excel", "CSV", "PDF"],
      submitted: false
    };
  },
  computed: {
    // ...mapGetters("compliance", ["getComplianceList"]),
    ...mapGetters("i18N", ["getLanguage"]),
    ...mapGetters("grid", ["getSelectedRows", "getLoading", "getTableHeaders", "getExportProgessAppearance"]),
    ...mapGetters("authfack", [
      "currentEntity",
      "userDetails",
    ]),
    ...mapGetters("emergency", [
      "getActiveSessionsCount",
    ]),
    rowsData() {
      return this.getComplianceList && this.getComplianceList.length > 0
        ? this.getComplianceList.map((el) => {
            return {
              name: el.name,
              version: el.version_id,
              date: el.created_at ? this.getDate(el.created_at) : "-",
              id: el.id,
              detail_list: el.form_metadata
                ? el.form_metadata.questions
                  ? el.form_metadata.questions
                  : el.form_metadata.data
                : "",
              showDetails: false,
            };
          })
        : [];
    },
    userEmail() {
      return this.userDetails && this.userDetails.email;
    },
    dataGrid() {
      return this.$refs[this.dataGridRef].instance;
    },
    hideAll() {
      return !Object.values(this.getTableHeaders).every((v) => v === false);
    },
  },
  watch: {
    getComplianceList: function () {
      document.querySelectorAll(".arrowIconToggle").forEach((el) => {
        el.parentElement.parentElement.classList.remove("selected-row");
        this.rowsData.map((el) => (el.showDetails = false));
      });
    },
    getExportProgessAppearance(value) {
      value && this.$bvModal.show("exportingProgress");
    },
  },
  methods: {
    ...mapActions("grid", [
      "onTableHeader",
      "updateTableHeaders",
      "createTableHeaders",
      "resetTableHeaders",
      "resetFilterValues",
      "exportJobBE"
    ]),
    ...mapActions("compliance", ["deleteCompliance"]),
    contentReady(e) {
      // console.log("ready", e);
      let sortedExp = this.getSortedColumns();
      let filterExp = e.component.getCombinedFilter();
      if (filterExp || sortedExp) {
        this.showClearAll = true;
      } else {
        this.showClearAll = false;
      }
      this.setColumnChooserPosition();
    },
    onEditorPreparing(e) {
      if (e.parentType == "filterRow") {
        e.editorOptions.placeholder = this.$t("common.grid.search");
      }
    },
    reset() {
      this.dataGrid.clearFilter("row");
      this.dataGrid.clearFilter("header");
      this.dataGrid.clearSorting();
      this.showClearAll = false;
      // this.dateRange = [];
      // this.dateChanged();
    },
    getSortedColumns() {
      let allColumns = Array.from(
        Array(this.dataGrid.columnCount()).keys()
      ).map((index) => this.dataGrid.columnOption(index));
      allColumns = allColumns.find((col) => col.sortIndex != null);
      return allColumns;
    },
    setColumnChooserPosition() {
      var columnChooserView = this.dataGrid.getView("columnChooserView");
      if (!columnChooserView._popupContainer) {
        columnChooserView._initializePopupContainer();
        columnChooserView.render();
      }
      columnChooserView._popupContainer.option("position", {
        of: this.dataGrid.element(),
        my: this.rtlEnabled ? "right top" : "left top",
        at: this.rtlEnabled ? "right top" : "left top",
        offset: "0 50",
      });
    },
    serverSideExport(e) {
      this.exportJobBE({
        type: e.itemData,
        // headers: columns.reduce((accumulator, value) => {
        //   return { ...accumulator, [value]: value };
        // }, {}),
        gridName: "compliance",
        entity_id: this.currentEntity.id,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        // filterBy: this.finalFilterObject,
        // orderBy: this.sortObject,
      });
    },
    async fetchCompliance() {
      const { data } = await complianceService.getCompliance();
      this.getComplianceList = data;
    },

    showCompliance(data) {
      this.complianceData = data;
    },
    getDate(date) {
        if (this.getLanguage === "ar")
        return moment
          .utc(date)
          .tz(moment.tz.guess())
          .locale('en') // Ensures numbers remain in English
          .format("DD/MM/YYYY - h:mm") + 
          (moment.utc(date).tz(moment.tz.guess()).format("A") === "AM" ? " ص" : " م");

      return moment
      .utc(date)
      .tz(moment.tz.guess())
      .format("DD/MM/YYYY - h:mm A");
    },
    onCheck(id, event) {
      let checkedIndex = this.rowsData.map((item) => item.id).indexOf(id);
      this.rowsData[checkedIndex].showDetails = event;
      document.querySelectorAll(".arrowIconToggle").forEach((el) => {
        el.childNodes[0].checked
          ? el.parentElement.parentElement.classList.add("selected-row")
          : el.parentElement.parentElement.classList.remove("selected-row");
      });
    },
    fetchTableHeaders() {
      this.onTableHeader({
        url: "/compliance-data-grid",
        headers: complianceColumns,
      });
    },
    async customLoad() {
      if (this.userEmail !== "lt_techteam@visipoint.com") {
        await this.onTableHeader({
          url: "/compliance-data-grid",
          // headers: reportHistoryColumns,
        });
        let value = this.getTableHeaders;
        if (value) {
          value.pageIndex = 0;
          value.pageSize = 25;
        }
        return value;
      }
    },
    customSave(gridState) {
      if (this.userEmail !== "lt_techteam@visipoint.com") {
        gridState.pageIndex = 0;
        gridState.pageSize = 25;
        gridState.selectedRowKeys = [];
        let value = JSON.stringify(gridState);
        value = value.replace(',"selectedRowKeys":[]', "");

        if (!this.getTableHeaders) {
          this.createTableHeaders({
            url: "/compliance-data-grid",
            headers: value,
            filters: "[]",
          });
        } else if (value != JSON.stringify(this.getTableHeaders)) {
          this.updateTableHeaders({
            url: "/compliance-data-grid",
            headers: value,
            filters: "[]",
          });
        }
      }
    },
    onDeleteCompliance() {
      this.submitted = true;
      this.deleteCompliance(this.complianceData.id).then(() => {
        this.fetchCompliance();
        this.$bvModal.hide('deleteComplianceConfirmModal');
        this.submitted = false;
      }).catch(() => {
        this.submitted = false;
      });
    }
  },
  async mounted() {
    if (this.getLanguage === "ar") {
      this.rtlEnabled = true;
    }
    this.fetchTableHeaders();
    this.fetchCompliance();
    document.title = this.$t("common.compliance");
  },
  destroyed() {
    this.resetTableHeaders();
    this.resetFilterValues();
  },
};
</script>

<style lang="scss">
.complianceGridHeight .dx-datagrid {
  height: calc(100vh - 204px);
}
.complianceGridHeightWithAlert .dx-datagrid {
  height: calc(100vh - 250px);
}
:dir(rtl) {
  .form-check-input {
    margin-right: -1.25rem;
    margin-left: unset !important;
  }
  .form-check {
    padding-right: 1.25rem;
    padding-left: unset !important;
  }
}
#complianceGrid {
  font-style: normal;
  margin-top: 15px;
  .grid-wrapper {
    border-radius: 8px;
  }
  .table-wrapper {
    height: calc(100vh - 354px);
  }
  .description-form input {
    width: 100%;
    padding-inline-start: 8px;
  }
  .selected-row {
    transition: all 0.4s ease;
    background-color: rgba(243, 243, 243, 0.6) !important;
    border: 1px solid rgba(0, 0, 0, 1) !important;
  }
  .answers-wrapper {
    .row-details {
      border: 1px solid rgb(235, 229, 229);
      border-top: none;
      border-radius: 10px;
      padding: 1% 3%;
    }
  }
  .answers-list {
    list-style: circle;
  }
  .arrowIconToggle {
    input {
      display: none !important;
    }
    .custom-checkbox .custom-control-input {
      background-color: #fff;
    }
    label.custom-control-label::before {
      border: none !important;
      background-color: transparent;
    }
    .custom-control-input {
      &:checked + .custom-control-label::after {
        transition: all 0.3s ease;
        transform: rotate(0deg) !important;
      }
    }
    .custom-control-label::after {
      transition: all 0.3s ease;
      background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Ctitle%3Edown-arrow%3C%2Ftitle%3E%3Cg%20fill%3D%22%23000000%22%3E%3Cpath%20d%3D%22M10.293%2C3.293%2C6%2C7.586%2C1.707%2C3.293A1%2C1%2C0%2C0%2C0%2C.293%2C4.707l5%2C5a1%2C1%2C0%2C0%2C0%2C1.414%2C0l5-5a1%2C1%2C0%2C1%2C0-1.414-1.414Z%22%20fill%3D%22%23000000%22%3E%3C%2Fpath%3E%3C%2Fg%3E%3C%2Fsvg%3E");
      background-size: 0.9em;
      @media only screen and (min-width: 670px) {
        transform: rotate(-90deg);
      }
    }
  }
}
</style>
